/* SHUFFLE DEMO engine + UI, the plugin dev's artifact script with theme-build patches marked 'theme build' (scratchpad shuffle_demo_build.py, 2026-10-20). Do not hand-edit: re-run the build. */
(() => {
'use strict';
// ============================================================ engine: a port of the plugin's FlipEngine
const SIZE_STEPS = [1, 2, 4, 8, 16], SIZE_NAMES = ['1 BAR', '1/2', '1/4', '1/8', '1/16'];
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const smooth = x => x * x * (3 - 2 * x);

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// ---- analysis: tempo from the name, the beat, or the length; then the slice grid
function bpmFromName(name) {
  const lower = (name || '').toLowerCase();
  const ok = n => (n >= 50 && n <= 220) ? n : 0;
  let m = lower.match(/(\d{2,3})\s*bpm/); if (m && ok(+m[1])) return +m[1];
  m = lower.match(/bpm[\s_-]*(\d{2,3})/); if (m && ok(+m[1])) return +m[1];
  m = lower.match(/(?:^|[_-])(\d{2,3})(?=[_-])/); if (m && ok(+m[1])) return +m[1];
  return 0;
}
function bpmFromOnsets(mono, sr, seconds) {
  const hop = 256, win = 512, n = mono.length;
  if (n < win * 8) return 0;
  const frames = Math.floor((n - win) / hop);
  const raw = new Float32Array(frames), flux = new Float32Array(frames);
  let prev = 0;
  for (let f = 0; f < frames; f++) {
    let e = 0; const s = f * hop;
    for (let i = 0; i < win; i++) { const v = mono[s + i]; e += v * v; }
    const db = 10 * Math.log10(Math.max(e / win, 1e-12));
    raw[f] = f === 0 ? 0 : Math.max(0, db - prev); prev = db;
  }
  const k = [1/9, 2/9, 3/9, 2/9, 1/9];
  for (let f = 0; f < frames; f++) for (let j = -2; j <= 2; j++) if (f + j >= 0 && f + j < frames) flux[f] += raw[f + j] * k[j + 2];
  let rises = 0; for (let f = 0; f < frames; f++) if (raw[f] > 3) rises++;
  if (rises < 4) return 0;
  let mean = 0; for (let f = 0; f < frames; f++) mean += flux[f]; mean /= frames;
  let energy = 0; for (let f = 0; f < frames; f++) { flux[f] -= mean; energy += flux[f] * flux[f]; }
  if (energy <= 1e-9) return 0;
  const hopSec = hop / sr, lagMin = Math.ceil(60 / 200 / hopSec), lagMax = Math.min(Math.floor(60 / 50 / hopSec), Math.floor(frames / 2) - 1);
  const acs = new Float64Array(lagMax + 2);
  let bestScore = 0, bestAc = 0, bestLag = 0;
  for (let lag = Math.max(1, lagMin - 1); lag <= lagMax + 1 && lag < frames; lag++) {
    let ac = 0; for (let f = lag; f < frames; f++) ac += flux[f] * flux[f - lag];
    ac /= energy; acs[lag] = ac;
    if (lag < lagMin || lag > lagMax) continue;
    const bpm = 60 / (lag * hopSec), x = Math.log2(bpm / 100), prior = Math.exp(-0.5 * x * x / 0.36);
    if (ac * prior > bestScore) { bestScore = ac * prior; bestAc = ac; bestLag = lag; }
  }
  if (!bestLag || bestAc < 0.1) return 0;
  let lag = bestLag;
  const a = acs[bestLag - 1], b = acs[bestLag], c = acs[bestLag + 1], den = a - 2 * b + c;
  if (Math.abs(den) > 1e-12) lag += clamp(0.5 * (a - c) / den, -0.5, 0.5);
  const est = 60 / (lag * hopSec);
  const whole = bpm => { const bars = seconds * bpm / 240; return bars >= 0.97 && Math.abs(bars - Math.round(bars)) < 0.03 * Math.max(1, bars); };
  for (const cand of [est, est / 2, est * 2]) if (cand >= 50 && cand <= 220 && whole(cand)) return Math.round(seconds * cand / 240) * 240 / seconds;
  return est;
}
function bpmFromLength(seconds) {
  const target = 120; let best = 1e9, bpm = target, bars = 1;
  for (const b of [1, 2, 3, 4, 6, 8, 12, 16, 24, 32]) {
    const cand = b * 240 / Math.max(seconds, 0.05); if (cand < 55 || cand > 200) continue;
    const score = Math.abs(Math.log(cand / target)); if (score < best) { best = score; bpm = cand; bars = b; }
  }
  return { bpm, bars };
}
function findOnsets(mono, sr) {
  const onsets = [], n = mono.length, hop = 256, win = 512;
  if (n < win * 2) return onsets;
  const frames = Math.floor((n - win) / hop), db = new Float32Array(frames);
  for (let f = 0; f < frames; f++) { let e = 0; const s = f * hop; for (let i = 0; i < win; i++) { const v = mono[s + i]; e += v * v; } db[f] = 10 * Math.log10(Math.max(e / win, 1e-12)); }
  const minGap = Math.ceil(0.05 * sr / hop); let last = -minGap;
  for (let f = 2; f < frames - 2; f++) {
    const flux = db[f] - db[f - 2];
    if (flux < 6 || db[f] < -45 || f - last < minGap) continue;
    let localMax = true;
    for (let k = f - 2; k <= f + 2; k++) if (k !== f && db[k] - db[Math.max(0, k - 2)] > flux) localMax = false;
    if (!localMax) continue;
    const s = f * hop; let peak = 0; for (let i = 0; i < win; i++) peak = Math.max(peak, Math.abs(mono[s + i]));
    let pos = s; for (let i = 0; i < win; i++) if (Math.abs(mono[s + i]) >= 0.2 * peak) { pos = s + i; break; }
    onsets.push(pos); last = f;
  }
  return onsets;
}
function analyse(sample, stepsPerBar) {
  const { mono, sr, name } = sample, seconds = mono.length / sr;
  const a = { stepsPerBar, bpm: 120, bars: 1, source: 'length' };
  const named = bpmFromName(name);
  if (named > 0) { a.bpm = named; a.source = 'named'; }
  else { const heard = bpmFromOnsets(mono, sr, seconds); if (heard > 0) { a.bpm = heard; a.source = 'heard'; } else { const l = bpmFromLength(seconds); a.bpm = l.bpm; a.bars = l.bars; } }
  if (a.source !== 'length') a.bars = Math.max(1, Math.round(seconds * a.bpm / 240));
  a.samplesPerStep = sr * 60 / a.bpm * 4 / stepsPerBar;
  a.onsets = findOnsets(mono, sr);
  const steps = a.bars * stepsPerBar, snap = Math.floor(0.03 * sr);
  a.sliceStarts = []; let prev = -1;
  for (let i = 0; i < steps; i++) {
    let pos = Math.round(i * a.samplesPerStep), bestDist = snap + 1;
    for (const o of a.onsets) { const d = Math.abs(o - pos); if (d < bestDist) { bestDist = d; pos = o; } }
    pos = clamp(pos, 0, Math.max(0, mono.length - 1)); if (pos <= prev) pos = prev + 1;
    a.sliceStarts.push(pos); prev = pos;
  }
  return a;
}

// ---- the plan
const KEEP_FLOOR = 0.15, REVERSE_MAX = 0.30, STUTTER_MAX = 0.25;
function roll(seed, numSteps, stepsPerBar, sourceSteps) {
  const rng = mulberry32(seed), dice = [];
  sourceSteps = Math.max(1, sourceSteps);
  const sourceBars = Math.max(1, Math.floor(sourceSteps / stepsPerBar));
  for (let k = 0; k < numSteps; k++) dice.push({ keep: rng(), choice: rng(), reverse: rng(), stutter: rng(), randomSlice: Math.floor(rng() * sourceSteps) % sourceSteps, otherBar: Math.floor(rng() * sourceBars) % sourceBars });
  return dice;
}
const plain = s => !s.reverse && !s.stutter;
function plan(dice, stepsPerBar, I, seed, sourceSteps) {
  I = clamp(I, 0, 1); sourceSteps = Math.max(1, sourceSteps);
  const p = { steps: [], stepsPerBar, bars: Math.max(1, Math.floor(dice.length / stepsPerBar)), sourceSteps, seed, intensity: I };
  for (let k = 0; k < dice.length; k++) {
    const d = dice[k], pos = k % stepsPerBar, s = { slice: 0, reverse: false, stutter: false, continues: false, edited: false, locked: false };
    const inPlace = k % sourceSteps;
    if (d.keep < 1 - (1 - KEEP_FLOOR) * I) s.slice = inPlace;
    else if (pos === 0 && d.choice < 0.85) s.slice = d.otherBar * stepsPerBar;
    else if (d.choice < 0.35 && k > 0) s.slice = p.steps[k - 1].slice;
    else if (d.choice < 0.60) s.slice = d.otherBar * stepsPerBar + pos;
    else s.slice = d.randomSlice;
    s.slice = clamp(s.slice, 0, sourceSteps - 1);
    s.reverse = d.reverse < REVERSE_MAX * I; s.stutter = d.stutter < STUTTER_MAX * I;
    if (k > 0) { const prev = p.steps[k - 1]; s.continues = plain(s) && plain(prev) && s.slice === prev.slice + 1; }
    p.steps.push(s);
  }
  return p;
}
function straightPlan(numSteps, stepsPerBar, sourceSteps) {
  sourceSteps = Math.max(1, sourceSteps);
  const p = { steps: [], stepsPerBar, bars: Math.max(1, Math.floor(numSteps / stepsPerBar)), sourceSteps, seed: 0, intensity: 0 };
  for (let k = 0; k < numSteps; k++) { const s = { slice: k % sourceSteps, reverse: false, stutter: false, continues: false, edited: false, locked: false }; s.continues = k > 0 && s.slice === p.steps[k - 1].slice + 1; p.steps.push(s); }
  return p;
}
function applyEdits(p, edits) {
  let any = false;
  for (const e of edits) {
    if (e.step < 0 || e.step >= p.steps.length) continue;
    p.steps[e.step] = { slice: clamp(e.plan.slice, 0, p.sourceSteps - 1), reverse: !!e.plan.reverse, stutter: !!e.plan.stutter, continues: false, edited: true, locked: !!e.locked };
    any = true;
  }
  if (!any) return;
  for (let k = 0; k < p.steps.length; k++) { const s = p.steps[k]; s.continues = k > 0 && plain(s) && plain(p.steps[k - 1]) && s.slice === p.steps[k - 1].slice + 1; }
}

// ---- the render: the loop at the sample's own tempo (bit-exact copies, no stretch needed)
function render(sample, an, opts) {
  const sr = sample.sr, spb = an.stepsPerBar, outSteps = Math.max(1, opts.bars) * spb;
  const p = opts.straight ? straightPlan(outSteps, spb, an.sliceStarts.length) : plan(roll(opts.seed, outSteps, spb, an.sliceStarts.length), spb, opts.intensity, opts.seed, an.sliceStarts.length);
  applyEdits(p, opts.edits || []);
  const spStep = an.samplesPerStep, stepStart = k => Math.round(k * spStep), length = stepStart(outSteps);
  const L = new Float32Array(length), R = new Float32Array(length);
  // Endpoint-inclusive quintic S curves: 1 ms in to keep drum attacks, 4 ms out, each capped at
  // half the segment; both channels share the gain; every cut starts and ends at exact silence.
  const edgeGain = (i, len) => { if (len <= 1) return 0; const t = i / (len - 1); return t * t * t * (10 + t * (-15 + 6 * t)); };
  const soften = (buf, from, len) => {
    if (len <= 0) return; if (len === 1) { buf[from] = 0; return; }
    const a = clamp(Math.round(0.001 * sr), 1, Math.floor(len / 2)), r = clamp(Math.round(0.004 * sr), 1, Math.floor(len / 2));
    for (let i = 0; i < a; i++) buf[from + i] *= edgeGain(i, a);
    for (let i = 0; i < r; i++) buf[from + len - r + i] *= edgeGain(r - 1 - i, r);
  };
  const src = [sample.L, sample.R];
  let k = 0;
  while (k < outSteps) {
    const first = p.steps[k]; let last = k;
    while (last + 1 < outSteps && p.steps[last + 1].continues) last++;
    const at = stepStart(k), runLen = stepStart(last + 1) - at; k = last + 1;
    if (runLen <= 0) continue;
    const bodyLen = first.stutter ? Math.max(1, Math.floor(runLen / 2)) : runLen;
    const start = an.sliceStarts[clamp(first.slice, 0, an.sliceStarts.length - 1)];
    const piece = [new Float32Array(runLen), new Float32Array(runLen)];
    for (let ch = 0; ch < 2; ch++) {
      const s = src[ch], out = piece[ch];
      for (let i = 0; i < bodyLen; i++) { const j = start + i; out[i] = j < s.length ? s[j] : 0; }
      if (first.reverse) { for (let i = 0, j = bodyLen - 1; i < j; i++, j--) { const t = out[i]; out[i] = out[j]; out[j] = t; } }
      if (first.stutter) {
        // the first half twice; an odd length repeats its last sample, then each half gets its own edges
        for (let i = 0; i < Math.min(bodyLen, runLen - bodyLen); i++) out[bodyLen + i] = out[i];
        if (runLen > bodyLen * 2) out[runLen - 1] = out[bodyLen - 1];
        soften(out, 0, bodyLen); soften(out, bodyLen, runLen - bodyLen);
      } else soften(out, 0, bodyLen);
      const dst = ch === 0 ? L : R;
      for (let i = 0; i < runLen; i++) dst[(at + i) % length] += out[i];
    }
  }
  return { L, R, sr, bpm: an.bpm, samplesPerStep: spStep, plan: p, length, stepStart };
}

// ============================================================ macros, rendered offline onto the loop
class SVF { // Zavalishin TPT state-variable filter, as JUCE's StateVariableTPTFilter
  constructor(sr) { this.sr = sr; this.s1 = 0; this.s2 = 0; this.g = 0; this.R2 = 2; this.h = 0; this.type = 'lp'; }
  set(fc, res, type) { this.g = Math.tan(Math.PI * clamp(fc, 10, this.sr * 0.49) / this.sr); this.R2 = 1 / res; this.h = 1 / (1 + this.R2 * this.g + this.g * this.g); this.type = type; }
  run(x) { const g = this.g, R2 = this.R2, h = this.h; const yHP = h * (x - this.s1 * (g + R2) - this.s2); const yBP = yHP * g + this.s1; this.s1 = yHP * g + yBP; const yLP = yBP * g + this.s2; this.s2 = yBP * g + yLP; return this.type === 'lp' ? yLP : yHP; }
}
function ageLaw(a) {
  a = clamp(a, 0, 1);
  const h = clamp((a - 0.6) / 0.4, 0, 1), f = clamp(a / 0.1, 0, 1);
  return { drive: 0.6 * a, resampleHz: 44100 * Math.pow(5000 / 44100, a), bits: 16 - 8 * a, wowMs: 3 * a, flutterMs: 0.2 * a,
    wowHz: 0.2 + 0.2 * a, flutterHz: 3.6 + 3.73 * a, cassette: 0.55 * 4 * a * (1 - a), vinyl: 0.6 * a * a, highPass: smooth(h), lofi: smooth(f) };
}
function applyAge(L, R, sr, a) {
  if (a <= 0.0005) return;
  const law = ageLaw(a), n = L.length, out = [L, R];
  const maxDelay = Math.ceil(0.03 * sr), delay = [new Float32Array(maxDelay), new Float32Array(maxDelay)]; let wpos = 0;
  const base = 0.006 * sr, msToSamples = 0.001 * sr;
  const pre = [[new SVF(sr), new SVF(sr)], [new SVF(sr), new SVF(sr)]], post = [[0,1,2,3].map(() => new SVF(sr)), [0,1,2,3].map(() => new SVF(sr))];
  const hp = [new SVF(sr), new SVF(sr)], noiseHp = [new SVF(sr), new SVF(sr)];
  const cutoff = clamp(0.45 * law.resampleHz, 200, 20000);
  for (let ch = 0; ch < 2; ch++) { pre[ch].forEach(f => f.set(cutoff, 0.5, 'lp')); post[ch].forEach(f => f.set(cutoff, 0.37, 'lp')); hp[ch].set(174.61, 0.5, 'hp'); noiseHp[ch].set(261.63, 0.5, 'hp'); }
  const holdInc = law.resampleHz / sr, qStep = 1 / Math.pow(2, law.bits);
  const wowInc = law.wowHz / sr, f1Inc = law.flutterHz / sr, f2Inc = law.flutterHz * 1.63 / sr;
  const gain = 2.95 * law.drive, comp = Math.pow(10, (-6 - 4 * law.drive) / 20), wet = law.drive < 0.25 ? law.drive * 4 : 1;
  const cassetteGain = law.cassette * law.cassette, vinylGain = law.vinyl * law.vinyl;
  const rng = mulberry32(20211);
  let wow = 0, fl1 = 0, fl2 = 0.33, drift = 0, hold = 1, held = [0, 0], pink = [0, 0, 0];
  const pinkNoise = () => { const w = rng() * 2 - 1; pink[0] = 0.99765 * pink[0] + w * 0.0990460; pink[1] = 0.96300 * pink[1] + w * 0.2965164; pink[2] = 0.57000 * pink[2] + w * 1.0526913; return (pink[0] + pink[1] + pink[2] + w * 0.1848) * 0.25; };
  let crackle = 0, crackleEnv = 0;
  for (let i = 0; i < n; i++) {
    wow += wowInc; if (wow >= 1) wow -= 1; fl1 += f1Inc; if (fl1 >= 1) fl1 -= 1; fl2 += f2Inc; if (fl2 >= 1) fl2 -= 1;
    drift += (rng() - 0.5) * 0.0004; drift = clamp(drift, -1, 1) * 0.99995;
    const modMs = law.wowMs * (0.75 * Math.sin(2 * Math.PI * wow) + 0.25 * drift) + law.flutterMs * (Math.sin(2 * Math.PI * fl1) + 0.5 * Math.sin(2 * Math.PI * fl2));
    const d = clamp(base + modMs * msToSamples, 1, maxDelay - 2);
    hold += holdInc; const sampleNow = hold >= 1; if (sampleNow) hold -= Math.floor(hold);
    // the noise bed: hiss for cassette, hiss plus sparse pops for vinyl (the plugin plays Origin's recordings)
    const hiss = pinkNoise();
    if (crackleEnv > 0) crackleEnv *= 0.985; else if (rng() < law.vinyl * 6 / sr) { crackle = (rng() - 0.5) * 0.9; crackleEnv = 1; }
    for (let ch = 0; ch < 2; ch++) {
      const x0 = out[ch][i];
      const line = delay[ch]; line[wpos] = x0;
      const rp = wpos - d; const i0 = Math.floor(rp), t = rp - i0; const r0 = line[((i0 % maxDelay) + maxDelay) % maxDelay], r1 = line[(((i0 + 1) % maxDelay) + maxDelay) % maxDelay];
      let x = r0 + (r1 - r0) * t;
      const driven = Math.tanh(x * (1 + 2.7 * gain)) * comp; x = x + (driven - x) * wet;
      const clean = x; let lofi = x;
      for (const f of pre[ch]) lofi = f.run(lofi);
      if (sampleNow) { const dither = Math.abs(lofi) > 1e-4 ? pinkNoise() * qStep * 0.5 : 0; held[ch] = Math.round((lofi + dither) / qStep) * qStep; }
      lofi = held[ch];
      for (const f of post[ch]) lofi = f.run(lofi);
      x = clean + (lofi - clean) * law.lofi;
      const hpv = hp[ch].run(x); x = x + (hpv - x) * law.highPass;
      const bed = 0.25118864 * (hiss * (0.05 * cassetteGain + 0.03 * vinylGain) + crackle * crackleEnv * vinylGain);   // -12 dB, as the plugin
      x += noiseHp[ch].run(bed);
      out[ch][i] = Number.isFinite(x) ? x : 0;
    }
    wpos = (wpos + 1) % maxDelay;
  }
}
function pumpGain(curve, phase, edge) {
  const rc = x => { x = clamp(x, 0, 1); return 0.5 - 0.5 * Math.cos(Math.PI * x); };
  if (curve === 0) { if (phase < edge) return 1 - 0.85 * rc(phase / edge); const t = (phase - edge) / (1 - edge); return 0.15 + 0.85 * (1 - Math.exp(-4 * t)) / (1 - Math.exp(-4)); }
  if (phase < edge) return 1 - rc(phase / edge); const t = (phase - edge) / (1 - edge), s = t * t * (3 - 2 * t); return s * s;
}
function applyMovement(L, R, sr, bpm, m) {
  m = clamp(m, 0, 1); if (m < 0.02) return;
  const curve = m < 0.5 ? 0 : 1, depth = m < 0.5 ? m * 1.4 : 0.7 + (m - 0.5) * 0.6;
  const cycle = 60 / bpm, inc = 1 / (cycle * sr), edge = clamp(0.004 / cycle, 0.02, 0.45), slew = 1 - Math.exp(-1 / (sr * 0.002));
  let phase = 0, gain = 1;
  for (let i = 0; i < L.length; i++) {
    const raw = pumpGain(curve, phase, edge), target = 1 + (raw - 1) * depth; gain += slew * (target - gain);
    L[i] *= gain; R[i] *= gain; phase += inc; if (phase >= 1) phase -= 1;
  }
}

// ============================================================ state
const state = {
  sample: null, analysis: null, loop: null, seed: 1, straight: true, edits: [], history: [], redo: [],
  bars: 4, sizeChoice: 3, intensity: 0.5, age: 0.3, movement: 0.25, hasUsedLocks: false, hovered: -1
};
const pushHistory = () => { state.history.push({ seed: state.seed, edits: state.edits.map(e => ({ ...e, plan: { ...e.plan } })), straight: state.straight, sizeChoice: state.sizeChoice }); if (state.history.length > 64) state.history.shift(); state.redo = []; };
const snapshotNow = () => ({ seed: state.seed, edits: state.edits.map(e => ({ ...e, plan: { ...e.plan } })), straight: state.straight, sizeChoice: state.sizeChoice });
const restore = s => { state.seed = s.seed; state.edits = s.edits; state.straight = s.straight; if (s.sizeChoice !== state.sizeChoice) { state.sizeChoice = s.sizeChoice; regrid(false); } };

function regrid(clearEdits) {
  if (!state.sample) return;
  state.analysis = analyse(state.sample, SIZE_STEPS[state.sizeChoice]);
  if (clearEdits) state.edits = [];
}
function flip() {
  if (!state.sample) return;
  captureBefore();
  pushHistory();
  state.seed = (Math.floor(Math.random() * 0x7fffffff) + 1) >>> 0;
  state.edits = state.edits.filter(e => e.locked);
  state.straight = false;
  rerender(true);
}
function undo() { if (!state.history.length) return; state.redo.push(snapshotNow()); restore(state.history.pop()); rerender(true); }
function redoAction() { if (!state.redo.length) return; state.history.push(snapshotNow()); restore(state.redo.pop()); rerender(true); }
function setLocked(step, locked) {
  const i = state.edits.findIndex(e => e.step === step);
  if (!locked) { if (i >= 0) state.edits.splice(i, 1); return; }
  if (i >= 0) { state.edits[i].locked = true; return; }
  if (!state.loop || step < 0 || step >= state.loop.plan.steps.length) return;
  const s = state.loop.plan.steps[step];
  state.edits.push({ step, plan: { slice: s.slice, reverse: s.reverse, stutter: s.stutter }, locked: true });
  state.hasUsedLocks = true;
}
const isLocked = step => state.edits.some(e => e.step === step && e.locked);

// ============================================================ audio: the flip on a transport
// A new sample restarts the sequence from the top (transport.t0); a new render while playing
// (FLIP, knobs, undo, BARS) crossfades in at the transport's position.
let ctx = null, current = null, playing = false;
const transport = { t0: 0 };
const ensureCtx = () => {
  if (!ctx) { ctx = new (window.AudioContext || window.webkitAudioContext)(); ctx.onstatechange = () => { updateControls(); draw(); }; }
  return ctx;
};
// Browsers hold audio back until the visitor interacts. The opening loop is armed on the transport
// right away; the first click, tap or key press anywhere resumes the context, and it starts from bar 1.
const isHeld = () => !!ctx && ctx.state !== 'running';
let heldAtPress = false;   // this press found the loop armed but held: its click means "let it play"
const unlock = () => { heldAtPress = playing && isHeld(); if (ctx && ctx.state !== 'running') ctx.resume(); };
['pointerdown', 'keydown', 'touchend'].forEach(ev => document.addEventListener(ev, unlock, { capture: true, passive: true }));
function makeBuffer(loop) {
  const buf = ctx.createBuffer(2, loop.length, loop.sr);
  buf.copyToChannel(loop.L, 0); buf.copyToChannel(loop.R, 1); return buf;
}
const posIn = (dur, when) => { const t = when - transport.t0; return t <= 0 ? 0 : t % dur; };
function voice(buf, when, offset, fadeIn) {
  const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
  const g = ctx.createGain(); src.connect(g); g.connect(ctx.destination);
  g.gain.setValueAtTime(fadeIn ? 0 : 1, when); if (fadeIn) g.gain.linearRampToValueAtTime(1, when + fadeIn);
  src.start(when, offset);
  return { src, gain: g, duration: buf.duration };
}
function release(v, when, fade = 0.02) { if (!v) return; v.gain.gain.cancelScheduledValues(when); v.gain.gain.setValueAtTime(v.gain.gain.value, when); v.gain.gain.linearRampToValueAtTime(0, when + fade); v.src.stop(when + fade + 0.01); }
const phaseOf = () => current && playing ? posIn(current.duration, ctx.currentTime) : 0;
// From the top of the sequence.
function startTransport() {
  ensureCtx(); if (ctx.state === 'suspended') ctx.resume();
  const now = ctx.currentTime;
  release(current, now); current = null;
  const t0 = now + 0.03; transport.t0 = t0;
  if (state.loop) current = voice(makeBuffer(state.loop), t0, 0, 0.004);
  playing = !!current;
  updateControls(); draw();
}
function swapFlip(loop) {
  if (!playing) return;
  const when = ctx.currentTime + 0.01, buf = makeBuffer(loop);
  const next = voice(buf, when, posIn(buf.duration, when), 0.02);
  release(current, when); current = next;
}
function stopTransport() {
  if (ctx) release(current, ctx.currentTime);
  current = null; playing = false;
}

// ============================================================ render pipeline: a Web Worker runs the engine and the
// macros, so the page never freezes; knob moves coalesce to the newest request while one is in flight.
const workerSource = `
const SIZE_STEPS = [1, 2, 4, 8, 16]; const KEEP_FLOOR = 0.15, REVERSE_MAX = 0.30, STUTTER_MAX = 0.25;
const clamp = ${clamp.toString()}; const smooth = ${smooth.toString()}; const plain = ${plain.toString()};
${mulberry32.toString()}
${roll.toString()}
${plan.toString()}
${straightPlan.toString()}
${applyEdits.toString()}
${render.toString()}
${SVF.toString()}
${ageLaw.toString()}
${applyAge.toString()}
${pumpGain.toString()}
${applyMovement.toString()}
let sample = null;
onmessage = e => {
  const m = e.data;
  if (m.type === 'sample') { sample = { L: m.L, R: m.R, sr: m.sr }; return; }
  if (m.type !== 'render' || !sample) return;
  const t0 = performance.now(); const loop = render(sample, m.analysis, m.opts); const t1 = performance.now();
  applyMovement(loop.L, loop.R, loop.sr, loop.bpm, m.movement); const t2 = performance.now();
  applyAge(loop.L, loop.R, loop.sr, m.age); const t3 = performance.now();
  postMessage({ type: 'loop', id: m.id, L: loop.L, R: loop.R, sr: loop.sr, bpm: loop.bpm, samplesPerStep: loop.samplesPerStep, plan: loop.plan, length: loop.length,
    timings: { renderMs: Math.round(t1 - t0), movementMs: Math.round(t2 - t1), ageMs: Math.round(t3 - t2) } }, [loop.L.buffer, loop.R.buffer]);
};`;
const worker = new Worker(URL.createObjectURL(new Blob([workerSource], { type: 'text/javascript' })));
let renderTimer = 0, renderId = 0, shownId = 0, inFlight = false, queued = null, restartOnNextLoop = false, loopWaiters = [];
const perf = window.__perf = { sentAt: {} };   // timings for the headless check
function postRender(req) { inFlight = true; req.id = ++renderId; perf.sentAt[req.id] = performance.now(); worker.postMessage(req); }
function rerender(immediate) {
  clearTimeout(renderTimer);
  const go = () => {
    if (!state.sample || !state.analysis) return;
    const a = state.analysis;
    const req = { type: 'render', analysis: { stepsPerBar: a.stepsPerBar, bpm: a.bpm, samplesPerStep: a.samplesPerStep, sliceStarts: a.sliceStarts },
      opts: { bars: state.bars, straight: state.straight, seed: state.seed, intensity: state.intensity, edits: state.edits.map(e => ({ step: e.step, plan: { ...e.plan }, locked: e.locked })) },
      age: state.age, movement: state.movement };
    if (inFlight) queued = req; else postRender(req);
  };
  if (immediate) go(); else renderTimer = setTimeout(go, 60);
}
worker.onmessage = e => {
  const m = e.data; inFlight = false; const tIn = performance.now(); perf.workerMs = Math.round(tIn - (perf.sentAt[m.id] || tIn)); perf.stages = m.timings;
  if (queued) { const q = queued; queued = null; postRender(q); }
  if (m.id < shownId) return;   // older than what is on screen
  shownId = m.id;
  const loop = { L: m.L, R: m.R, sr: m.sr, bpm: m.bpm, samplesPerStep: m.samplesPerStep, plan: m.plan, length: m.length, stepStart: k => Math.round(k * m.samplesPerStep) };
  state.loop = loop;
  if (restartOnNextLoop) { restartOnNextLoop = false; startTransport(); }   // a new sample: from the top
  else if (playing) swapFlip(loop);
  const waiters = loopWaiters; loopWaiters = []; waiters.forEach(f => f());
  const t1 = performance.now(); onNewLoop(); const t2 = performance.now(); draw(); const t3 = performance.now(); updateControls();
  perf.onNewLoopMs = Math.round(t2 - t1); perf.drawMs = Math.round(t3 - t2); perf.messageTotalMs = Math.round(performance.now() - tIn);
};

// ============================================================ UI: the Black Glass skin at the plugin's geometry
const $ = id => document.getElementById('shf-' + id);   // theme build: ids are prefixed in the snippet
const panel = $('panel'), frame = $('frame'), screen = $('screen'), canvas = $('wave'), g2 = canvas.getContext('2d');
const lockbtn = $('lockbtn'), note = $('note'), empty = $('empty'), flipBtn = $('flip'), playBtn = $('play');
function fit() { const s = frame.clientWidth / 760; panel.style.transform = `scale(${s})`; frame.style.height = `${440 * s}px`; }
window.addEventListener('resize', fit); fit(); window.addEventListener('load', fit); setTimeout(fit, 400);   // theme build: re-fit after the page's own styles settle (the stage is 836px on desktop)

const DPR = Math.min(2, window.devicePixelRatio || 1);
canvas.width = 734 * DPR; canvas.height = 158 * DPR;
const inner = { x: 4, y: 4, w: 726, h: 150 };   // the screen bounds reduced by 4, as the plugin's layout()
const cellX = (loop, k) => inner.x + Math.floor(loop.stepStart(k) * (inner.w - 1) / loop.length);
function stepAt(x) { if (!state.loop) return -1; const sample = Math.floor(clamp(x - inner.x, 0, inner.w - 1) * state.loop.length / Math.max(1, inner.w - 1)); for (let k = state.loop.plan.steps.length - 1; k >= 0; k--) if (sample >= state.loop.stepStart(k)) return k; return 0; }
const peaksCache = new WeakMap();
function peaks(loop) {   // two columns per logical pixel, as the plugin samples the waveform
  let pk = peaksCache.get(loop); if (pk) return pk;
  const columns = inner.w * 2, n = loop.length; pk = new Float32Array(columns * 2);
  for (let c = 0; c < columns; c++) {
    const from = Math.floor(c * n / columns), to = Math.max(from + 1, Math.floor((c + 1) * n / columns)); let lo = 0, hi = 0;
    for (let i = from; i < to; i++) { const l = loop.L[i], r = loop.R[i]; if (l < lo) lo = l; if (l > hi) hi = l; if (r < lo) lo = r; if (r > hi) hi = r; }
    pk[c * 2] = lo; pk[c * 2 + 1] = hi;
  }
  peaksCache.set(loop, pk); return pk;
}
// The grid and the flat waveform change only when the loop does: they live on offscreen layers
// and each frame blits them, so playback and the tile animation cost a few rectangles per frame.
const layers = { loop: null, grid: null, wave: null };
function layerFor(loop) {
  if (layers.loop === loop) return layers;
  const make = () => { const c = document.createElement('canvas'); c.width = 734 * DPR; c.height = 158 * DPR; const g = c.getContext('2d'); g.setTransform(DPR, 0, 0, DPR, 0, 0); return [c, g]; };
  const [grid, gg] = make(), [wave, wg] = make();
  const steps = loop.plan.steps.length;
  for (let k = 0; k < steps; k++) {
    const x = cellX(loop, k) + 0.5;
    if (k % loop.plan.stepsPerBar === 0) { gg.strokeStyle = 'rgba(228,232,235,.38)'; gg.setLineDash([]); } else { gg.strokeStyle = 'rgba(228,232,235,.16)'; gg.setLineDash([2, 2]); }
    gg.lineWidth = 1; gg.beginPath(); gg.moveTo(x, inner.y); gg.lineTo(x, inner.y + inner.h); gg.stroke();
  }
  const pk = peaks(loop), mid = inner.y + Math.floor(inner.h / 2);
  const grad = wg.createLinearGradient(0, mid - inner.h * 0.4, 0, mid + inner.h * 0.4); grad.addColorStop(0, 'rgba(237,243,247,1)'); grad.addColorStop(1, 'rgba(169,186,201,1)'); wg.fillStyle = grad;
  for (let c = 0; c < pk.length / 2; c++) { const lo = pk[c * 2], hi = pk[c * 2 + 1]; wg.fillRect(inner.x + c * 0.5, mid - hi * inner.h * 0.46, 0.5, (hi - lo) * inner.h * 0.46 + 0.5); }
  layers.loop = loop; layers.grid = grid; layers.wave = wave; return layers;
}
function waveFill(top, bottom, alpha) { const grad = g2.createLinearGradient(0, top, 0, bottom); grad.addColorStop(0, `rgba(237,243,247,${alpha})`); grad.addColorStop(1, `rgba(169,186,201,${alpha})`); return grad; }
function drawFlat(loop) { g2.drawImage(layerFor(loop).wave, 0, 0, 734, 158); }
function drawLockGlyph(cx, y, w, locked, color) {
  const s = Math.min(w / 12, 14 / 16); g2.strokeStyle = color; g2.fillStyle = color; g2.lineWidth = 1.25 * s; g2.lineCap = 'round';
  const bx = cx - 6 * s, by = y;
  g2.beginPath(); g2.roundRect(bx + s, by + 7 * s, 10 * s, 8 * s, 1.6 * s); g2.stroke();
  g2.beginPath(); g2.moveTo(bx + 3 * s, by + 7 * s); g2.lineTo(bx + 3 * s, by + 4 * s); g2.bezierCurveTo(bx + 3 * s, by - s, bx + 9 * s, by - s, bx + 9 * s, by + 4 * s); if (locked) g2.lineTo(bx + 9 * s, by + 7 * s); g2.stroke();
  g2.beginPath(); g2.arc(bx + 6 * s, by + 10.85 * s, 0.85 * s, 0, Math.PI * 2); g2.fill();
}

// ---- the FLIP tile animation, after the plugin's SliceTransition: changed slices lift as upright
// tiles and travel to their new place; new ones arrive from the nearest end; locked and unchanged stay
const anim = { active: false, start: 0, before: null, after: null, maps: null, used: null };
const TRAVEL_MS = 920;
const ramp = (p, a, b) => smooth(clamp((p - a) / (b - a), 0, 1));
const liftAt = p => ramp(p, 0, 0.22) * (1 - ramp(p, 0.74, 1));
const mix = (a, b, t) => a + (b - a) * t;
function sliceCache(loop) {
  const pk = peaks(loop), cols = pk.length / 2, mid = inner.y + Math.floor(inner.h / 2), out = [];
  for (let k = 0; k < loop.plan.steps.length; k++) {
    const x0 = cellX(loop, k), x1 = k + 1 === loop.plan.steps.length ? inner.x + inner.w : cellX(loop, k + 1), w = Math.max(0.5, x1 - x0);
    const start = clamp(Math.floor((x0 - inner.x) * 2), 0, cols), end = clamp(Math.ceil((x1 - inner.x) * 2), start, cols), columns = [];
    for (let c = start; c < end; c++) { const x = inner.x + c * 0.5, cx0 = Math.max(x, x0), cx1 = Math.min(x + 0.5, x1); const lo = pk[c * 2], hi = pk[c * 2 + 1];
      const top = mid - hi * inner.h * 0.46, h = (hi - lo) * inner.h * 0.46 + 0.5;
      columns.push({ u0: (cx0 - x0) / w, u1: (cx1 - x0) / w, v0: (top - inner.y) / inner.h, v1: (top + h - inner.y) / inner.h, flat: [cx0, top, cx1 - cx0, h] }); }
    out.push({ x: x0, w, plan: loop.plan.steps[k], columns, locked: !!loop.plan.steps[k].locked });
  }
  return out;
}
let beforeLoop = null;
function captureBefore() { beforeLoop = (state.loop && !anim.active) ? state.loop : null; }
function onNewLoop() {
  if (!beforeLoop || !state.loop || state.straight || beforeLoop.plan.steps.length !== state.loop.plan.steps.length || beforeLoop.plan.stepsPerBar !== state.loop.plan.stepsPerBar) { beforeLoop = null; cancelAnim(); return; }
  const before = sliceCache(beforeLoop), after = sliceCache(state.loop), locks = after.map((s, i) => isLocked(i) || s.locked), maps = [], uses = new Array(before.length).fill(0);
  let movement = false;
  const same = (a, b) => a.slice === b.slice && a.reverse === b.reverse && a.stutter === b.stutter;
  for (let i = 0; i < after.length; i++) { const stationary = locks[i] || same(before[i].plan, after[i].plan); maps.push({ source: stationary ? i : -1, stationary, locked: locks[i] }); if (stationary) uses[i]++; else movement = true; }
  for (let d = 0; d < after.length; d++) {
    const m = maps[d]; if (m.stationary) continue; let best = -1, bestScore = Infinity;
    for (let s = 0; s < before.length; s++) { const c = before[s].plan; if (c.slice !== after[d].plan.slice) continue; const diff = (c.reverse !== after[d].plan.reverse) + (c.stutter !== after[d].plan.stutter); const score = diff * 10000 + uses[s] * 512 + Math.abs(s - d); if (score < bestScore) { bestScore = score; best = s; } }
    m.source = best; if (best >= 0) uses[best]++;
  }
  beforeLoop = null;
  if (!movement || before.length > 128) { cancelAnim(); return; }
  anim.active = true; anim.start = performance.now(); anim.before = before; anim.after = after; anim.maps = maps; anim.used = uses.map(u => u > 0);
  state.hovered = -1; updateLockControl();
}
function cancelAnim() { anim.active = false; anim.before = anim.after = anim.maps = null; }
const outsideX = s => { const gap = 12 + s.w * 0.05; return (s.x + s.w / 2 < inner.x + inner.w / 2) ? inner.x - s.w - gap : inner.x + inner.w + gap; };
function pose(originX, originW, destX, destW, index, lift, travel) {
  const lane = ((index * 37 + 11) % 13) / 12;
  return { cx: mix(originX + originW / 2, destX + destW / 2, travel), cy: inner.y + inner.h / 2 - lift * (6 + lane * 3), w: mix(originW, destW, travel), h: inner.h * (1 - lift * 0.2), depth: lift * (12 + lane * 8), dir: destX >= originX ? 1 : -1 };
}
function project(p, u, v, localDepth = 0) { const x = (u - 0.5) * p.w, y = (v - 0.5) * p.h, per = 460 / Math.max(180, 460 - p.depth - localDepth); return [p.cx + x * per - localDepth * 0.55, p.cy + y * per - localDepth * 0.9]; }
function drawProjected(slice, p, alpha) {
  if (alpha <= 0.001) return;
  g2.beginPath(); let first = true;
  for (const c of slice.columns) { const [ax, ay] = project(p, c.u0, c.v0), [bx] = project(p, c.u1, c.v0); if (first) { g2.moveTo(ax, ay); first = false; } else g2.lineTo(ax, ay); g2.lineTo(bx, ay); }
  for (let i = slice.columns.length - 1; i >= 0; i--) { const c = slice.columns[i]; const [bx, by] = project(p, c.u1, c.v1), [ax] = project(p, c.u0, c.v1); g2.lineTo(bx, by); g2.lineTo(ax, by); }
  g2.closePath(); const [, t] = project(p, 0.5, 0.1), [, b] = project(p, 0.5, 0.9); g2.fillStyle = waveFill(t, b, alpha); g2.fill();
}
function drawTile(item, lift) {
  const p = item.pose, [tlx, tly] = project(p, 0, 0), [trx] = project(p, 1, 0), [, bry] = project(p, 1, 1);
  const fw = trx - tlx, fh = bry - tly, material = lift;
  if (material > 0.001 && p.w < 12) { g2.fillStyle = `rgba(25,35,40,${0.94 * material})`; g2.fillRect(tlx, tly, fw, fh); g2.fillStyle = `rgba(84,219,228,${0.38 * material})`; g2.fillRect(p.dir > 0 ? trx - 0.65 : tlx, tly, 0.65, fh); }
  else if (material > 0.001) {
    g2.fillStyle = `rgba(0,0,0,${0.15 * material})`; g2.fillRect(tlx + lift * 4, tly + lift * 8, fw, fh);
    const th = 3.5 * lift; g2.fillStyle = `rgba(8,14,17,${material})`;
    g2.beginPath(); g2.moveTo(trx, tly); g2.lineTo(trx + th * 0.55, tly + th * 0.9); g2.lineTo(trx + th * 0.55, bry + th * 0.9); g2.lineTo(trx, bry); g2.closePath(); g2.fill();
    g2.beginPath(); g2.moveTo(tlx, bry); g2.lineTo(trx, bry); g2.lineTo(trx + th * 0.55, bry + th * 0.9); g2.lineTo(tlx + th * 0.55, bry + th * 0.9); g2.closePath(); g2.fill();
    const grad = g2.createLinearGradient(tlx, tly, trx, bry); grad.addColorStop(0, `rgba(32,43,48,${0.94 * material})`); grad.addColorStop(1, `rgba(16,24,28,${0.94 * material})`); g2.fillStyle = grad; g2.fillRect(tlx, tly, fw, fh);
    g2.strokeStyle = `rgba(84,219,228,${0.42 * material})`; g2.lineWidth = 0.7; g2.strokeRect(tlx + 0.35, tly + 0.35, fw - 0.7, fh - 0.7);
    g2.strokeStyle = `rgba(255,255,255,${0.13 * material})`; g2.lineWidth = 0.6; g2.beginPath(); g2.moveTo(tlx, tly); g2.lineTo(trx, tly); g2.stroke();
  }
  if (item.source >= 0 && item.blend < 1) drawProjected(anim.before[item.source], p, 1 - item.blend);
  if (item.dest >= 0 && item.blend > 0) drawProjected(anim.after[item.dest], p, item.blend);
}
function drawAnim(progress) {
  const p = clamp(progress, 0, 1); if (p >= 1) { drawFlat(state.loop); return; }
  const lift = liftAt(p), travel = ramp(p, 0.17, 0.82), blend = ramp(p, 0.39, 0.70), items = [];
  anim.before.forEach((s, i) => { if (!anim.used[i] && !s.locked) items.push({ source: i, dest: -1, blend: 0, pose: pose(s.x, s.w, outsideX(s), s.w, i, lift, travel) }); });
  anim.maps.forEach((m, d) => { if (m.stationary) return; const t = anim.after[d]; const o = m.source >= 0 ? anim.before[m.source] : { x: outsideX(t), w: t.w }; items.push({ source: m.source, dest: d, blend: m.source >= 0 ? blend : 1, pose: pose(o.x, o.w, t.x, t.w, d, lift, travel) }); });
  items.sort((a, b) => a.pose.depth - b.pose.depth);
  g2.save();
  anim.maps.forEach((m, i) => { if (m.locked) { const s = anim.after[i]; g2.beginPath(); g2.rect(0, 0, 734, 158); g2.rect(s.x, inner.y, s.w, inner.h); g2.clip('evenodd'); } });
  for (const it of items) drawTile(it, lift);
  g2.restore();
  const mid = inner.y + Math.floor(inner.h / 2); g2.fillStyle = waveFill(mid - inner.h * 0.4, mid + inner.h * 0.4, 1);
  anim.maps.forEach((m, i) => { if (m.stationary) for (const c of anim.before[i].columns) g2.fillRect(...c.flat); });
}

function draw() {
  g2.setTransform(DPR, 0, 0, DPR, 0, 0); g2.clearRect(0, 0, 734, 158);
  const loop = state.loop; empty.hidden = !!state.sample;
  if (!loop) { note.textContent = ''; return; }
  g2.save(); g2.beginPath(); g2.roundRect(inner.x, inner.y, inner.w, inner.h, 3); g2.clip();
  const steps = loop.plan.steps.length;
  if (!anim.active && state.hovered >= 0 && state.hovered < steps) { g2.fillStyle = 'rgba(228,232,235,.055)'; g2.fillRect(cellX(loop, state.hovered), inner.y, cellX(loop, state.hovered + 1) - cellX(loop, state.hovered), inner.h); }
  for (let k = 0; k < steps; k++) if (isLocked(k)) { g2.fillStyle = 'rgba(84,219,228,.13)'; g2.fillRect(cellX(loop, k), inner.y, Math.max(1, cellX(loop, k + 1) - cellX(loop, k)), inner.h); }
  g2.drawImage(layerFor(loop).grid, 0, 0, 734, 158);
  if (anim.active) { const p = (performance.now() - anim.start) / TRAVEL_MS; drawAnim(p); if (p >= 1) { cancelAnim(); updateLockControl(); } }
  else drawFlat(loop);
  if (playing && current) {
    const x = inner.x + Math.floor(phaseOf() / current.duration * (inner.w - 1));
    g2.fillStyle = 'rgba(36,219,230,.10)'; g2.fillRect(x - 2, inner.y, 5, inner.h);
    g2.fillStyle = '#24dbe6'; g2.fillRect(x, inner.y, 1.3, inner.h);
    g2.beginPath(); g2.moveTo(x - 4, inner.y); g2.lineTo(x + 6, inner.y); g2.lineTo(x + 1, inner.y + 6); g2.closePath(); g2.fill();
  }
  for (let k = 0; k < steps; k++) if (isLocked(k)) {
    const x0 = cellX(loop, k), w = Math.max(1, cellX(loop, k + 1) - x0);
    g2.fillStyle = 'rgba(84,219,228,.65)'; g2.fillRect(x0, inner.y, w, 2);
    if (k !== state.hovered) drawLockGlyph(x0 + w / 2, inner.y + 11, Math.min(10, w - 1), true, '#54dbe4');
  }
  g2.restore();
  const noteText = `${state.straight ? 'SOURCE' : 'FLIP'}  ${loop.plan.bars} BARS  ${steps} STEPS`;
  if (note.textContent !== noteText) note.textContent = noteText;
}
function updateLockControl() {
  const ok = !anim.active && state.loop && state.hovered >= 0 && state.hovered < state.loop.plan.steps.length;
  lockbtn.classList.toggle('show', !!ok);
  if (!ok) return;
  const x0 = cellX(state.loop, state.hovered), x1 = cellX(state.loop, state.hovered + 1);
  lockbtn.style.left = `${clamp((x0 + x1) / 2 - 12, inner.x, inner.x + inner.w - 24)}px`;
  const locked = isLocked(state.hovered);
  lockbtn.classList.toggle('locked', locked);
  $('shackle').setAttribute('d', locked ? 'M3 7 V4 C3 1.5 9 1.5 9 4 V7' : 'M3 7 V4 C3 1.5 9 1.5 9 4');
  lockbtn.setAttribute('aria-label', (locked ? 'Unlock slice ' : 'Lock slice ') + (state.hovered + 1));
}
function updateControls() {
  flipBtn.disabled = !state.sample; playBtn.disabled = !state.loop;
  panel.classList.toggle('ready', !!state.sample);                     // the lettering and the aura breathe together
  panel.classList.toggle('invite', !!state.sample && state.straight && playing && !isHeld());  // once it sounds, FLIP is the next step
  $('undo').disabled = !state.history.length; $('redo').disabled = !state.redo.length;
  $('bars4').classList.toggle('on', state.bars === 4); $('bars8').classList.toggle('on', state.bars === 8);
  $('bars4').setAttribute('aria-pressed', state.bars === 4); $('bars8').setAttribute('aria-pressed', state.bars === 8);
  const sounding = playing && !isHeld();
  $('playLabel').textContent = sounding ? 'STOP' : 'PLAY';
  $('playIcon').setAttribute('d', sounding ? 'M4.5 4.5 H15.5 V15.5 H4.5 Z' : 'M5 3.2 L17 10 L5 16.8 Z');
  playBtn.setAttribute('aria-label', sounding ? 'Stop' : 'Play');
  playBtn.classList.toggle('playing', sounding);
  playBtn.classList.toggle('call', !!state.loop && !sounding);   // the obvious next step while nothing sounds
  $('dragAudio').classList.toggle('ready', !!state.loop);   // shown ready as in the plugin; dragging out is plugin-only
  updateLockControl();
}

// ---- the waveform: hover reveals the padlock. Click it to toggle; press it and drag sideways to
// apply its starting action (lock from an open padlock, unlock from a closed one) to every slice
// crossed. Drags must start on the padlock; the waveform body and right-click never change locks.
const screenPoint = e => { const r = screen.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width * 734, y: (e.clientY - r.top) / r.height * 158 }; };
const gesture = { active: false, pointerId: -1, startStep: -1, lastStep: -1, action: true, dragging: false, painted: false, clickable: false, pressX: 0, pressY: 0, loop: null };
function endGesture() { gesture.active = false; gesture.loop = null; lockbtn.classList.remove('down'); }
function cancelGesture() { if (!gesture.active) return; endGesture(); state.hovered = -1; updateLockControl(); draw(); }
screen.addEventListener('pointermove', e => {
  if (!state.loop || anim.active || gesture.active) return;
  const lb = lockbtn.getBoundingClientRect();
  if (lockbtn.classList.contains('show') && e.clientX >= lb.left && e.clientX <= lb.right && e.clientY >= lb.top && e.clientY <= lb.bottom) return;   // keep the badge's own slice
  state.hovered = stepAt(screenPoint(e).x); updateLockControl(); draw();
});
screen.addEventListener('pointerleave', () => { if (gesture.active || document.activeElement === screen) return; state.hovered = -1; updateLockControl(); draw(); });
lockbtn.addEventListener('pointerdown', e => {
  if (e.button !== 0 || e.ctrlKey || e.metaKey || anim.active || !state.loop || state.hovered < 0) return;
  e.preventDefault(); e.stopPropagation(); try { lockbtn.setPointerCapture(e.pointerId); } catch (err) { /* events still arrive while over the badge */ }
  const p = screenPoint(e);
  Object.assign(gesture, { active: true, pointerId: e.pointerId, startStep: state.hovered, lastStep: state.hovered, action: !isLocked(state.hovered),
    dragging: false, painted: false, clickable: true, pressX: p.x, pressY: p.y, loop: state.loop });
  lockbtn.classList.add('down');
});
lockbtn.addEventListener('pointermove', e => {
  if (!gesture.active || e.pointerId !== gesture.pointerId) return;
  if (state.loop !== gesture.loop || anim.active || (e.pointerType === 'mouse' && e.buttons !== 1)) return cancelGesture();
  const p = screenPoint(e);
  if (Math.hypot(p.x - gesture.pressX, p.y - gesture.pressY) > 4) gesture.clickable = false;
  if (Math.abs(p.x - gesture.pressX) > 4) gesture.dragging = true;
  if (p.y < inner.y || p.y >= inner.y + inner.h) { gesture.lastStep = -1; gesture.clickable = false; return; }   // outside: pause; re-entry resumes here
  if (!gesture.dragging) return;
  const step = stepAt(p.x);   // clamps at both grid edges
  if (!gesture.painted) { setLocked(gesture.startStep, gesture.action); gesture.painted = true; }
  const from = gesture.lastStep < 0 ? step : gesture.lastStep;
  for (let k = Math.min(from, step); k <= Math.max(from, step); k++) setLocked(k, gesture.action);   // fast jumps fill the gap
  gesture.lastStep = step; updateLockControl(); draw();
});
lockbtn.addEventListener('pointerup', e => {
  if (!gesture.active || e.pointerId !== gesture.pointerId) return;
  const r = lockbtn.getBoundingClientRect(), inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  const click = gesture.clickable && !gesture.dragging && inside && state.loop === gesture.loop, step = gesture.startStep;
  endGesture();
  if (click) setLocked(step, !isLocked(step));
  state.hovered = inside ? step : stepAt(screenPoint(e).x); updateLockControl(); draw();
});
lockbtn.addEventListener('pointercancel', cancelGesture);
lockbtn.addEventListener('lostpointercapture', () => { if (gesture.active) cancelGesture(); });
// keyboard: arrows choose a slice, Space or Return locks or unlocks it
screen.tabIndex = 0; screen.setAttribute('role', 'group'); screen.setAttribute('aria-label', 'Sample slices. Use the arrow keys to choose a slice, then Space or Return to lock or unlock it.');
screen.addEventListener('keydown', e => {
  if (!state.loop || anim.active || gesture.active) return;
  const n = state.loop.plan.steps.length;
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { state.hovered = clamp(state.hovered < 0 ? 0 : state.hovered + (e.key === 'ArrowLeft' ? -1 : 1), 0, n - 1); e.preventDefault(); }
  else if ((e.key === ' ' || e.key === 'Enter') && state.hovered >= 0) { setLocked(state.hovered, !isLocked(state.hovered)); e.preventDefault(); }
  else return;
  updateLockControl(); draw();
});
screen.addEventListener('focus', () => { if (state.loop && state.hovered < 0 && !gesture.active) { state.hovered = 0; updateLockControl(); draw(); } });
screen.addEventListener('blur', () => { if (!screen.matches(':hover') && !gesture.active) { state.hovered = -1; updateLockControl(); draw(); } });

// ---- header tips: only the header controls, after 550 ms, inside the panel, at a legible size whatever the zoom
const tip = $('tip'); let tipTimer = 0, tipFor = null;
function hideTip() { clearTimeout(tipTimer); tipTimer = 0; tipFor = null; tip.hidden = true; }
document.querySelectorAll('[data-tip]').forEach(el => {
  el.addEventListener('pointerenter', e => {
    hideTip(); tipFor = el; const x0 = e.clientX, y0 = e.clientY;
    tipTimer = setTimeout(() => {
      if (tipFor !== el) return;
      tip.textContent = el.dataset.tip; tip.hidden = false;
      const area = panel.getBoundingClientRect(), w = tip.offsetWidth, h = tip.offsetHeight, m = 6;
      let x = x0 + w + 16 <= area.right - m ? x0 + 16 : x0 - w - 12, y = y0 + h + 20 <= area.bottom - m ? y0 + 20 : y0 - h - 12;
      x = clamp(x, area.left + m, Math.max(area.left + m, area.right - m - w)); y = clamp(y, area.top + m, Math.max(area.top + m, area.bottom - m - h));
      tip.style.left = `${x}px`; tip.style.top = `${y}px`;
    }, 550);
  });
  el.addEventListener('pointerleave', hideTip); el.addEventListener('pointerdown', hideTip);
});
window.addEventListener('scroll', hideTip, { passive: true });
// drag audio and power are shown as in the plugin and do nothing here
['dragAudio', 'power'].forEach(id => $(id).addEventListener('click', e => e.preventDefault()));

// ---- the header strip: the backplate's clean material stretched over the old key housings, then
// the original housings moved under the compact controls, each with feathered edges; two dividers.
function buildHeaderArt() {
  const img = new Image();
  img.onload = () => {
    const c = $('headerArt'), g = c.getContext('2d'), S = 2;   // drawn at twice the logical size
    const kx = img.naturalWidth / 760, ky = img.naturalHeight / 440;
    const region = (tx, ty, tw, th, sx, sy, sw, sh) => {
      const f = document.createElement('canvas'); f.width = tw * S; f.height = th * S;
      const fg = f.getContext('2d', { willReadFrequently: true }); fg.imageSmoothingQuality = 'high';
      fg.drawImage(img, Math.round(sx * kx), Math.round(sy * ky), Math.round(sw * kx), Math.round(sh * ky), 0, 0, f.width, f.height);
      try {
        const d = fg.getImageData(0, 0, f.width, f.height), px = d.data, feather = 4 * S;
        for (let y = 0; y < f.height; y++) for (let x = 0; x < f.width; x++) {
          const t = clamp(Math.min(x, y, f.width - 1 - x, f.height - 1 - y) / feather, 0, 1), k = (y * f.width + x) * 4 + 3;
          px[k] = px[k] * t * t * (3 - 2 * t);
        }
        fg.putImageData(d, 0, 0);
      } catch (err) { /* a tainted canvas keeps hard edges */ }
      g.drawImage(f, tx * S, ty * S);
    };
    region(430, 12, 317, 36, 430, 12, 100, 36);   // clean material over the old housings
    region(486, 12, 65, 36, 539, 12, 73, 36);     // undo / redo housing
    region(597, 12, 64, 36, 671, 12, 73, 36);     // 4 / 8 housing
    g.fillStyle = 'rgba(228,231,233,.22)';
    for (const x of [556, 666]) g.fillRect(x * S, 21 * S, 1 * S, 14 * S);
  };
  img.src = (getComputedStyle($('panel')).backgroundImage.match(/url\("?(.*?)"?\)/) || [])[1] || 'panel.png';
}
buildHeaderArt();
// Only the loop tiles can be dropped on the page; files from the computer are not taken (no upload).
let toastTimer = 0;
function toast(message) { const t = $('toast'); t.textContent = message; t.hidden = false; clearTimeout(toastTimer); toastTimer = setTimeout(() => { t.hidden = true; }, 4500); }
function entryFrom(dt) {   // a tray chip: a JSON payload, also as text/plain for browsers that drop custom types
  for (const type of ['text/x-sample', 'text/plain']) {
    const text = dt.getData(type); if (!text || !text.includes('{')) continue;
    try { const entry = JSON.parse(text.slice(text.indexOf('{'))); if (entry && entry.file) return entry; } catch (e) { /* not ours */ }
  }
  return null;
}
const isTileDrag = e => !!e.dataTransfer && [...(e.dataTransfer.types || [])].includes('text/x-sample');
function handleDrop(e) {
  e.preventDefault(); screen.classList.remove('drag');   // also stops a dropped file from opening in the tab
  const entry = e.dataTransfer && entryFrom(e.dataTransfer); if (entry) return pickEntry(entry, { fly: false });
}
['dragenter', 'dragover'].forEach(ev => document.addEventListener(ev, e => {
  e.preventDefault();
  const tile = isTileDrag(e);
  if (e.dataTransfer) e.dataTransfer.dropEffect = tile ? 'copy' : 'none';
  screen.classList.toggle('drag', tile);
}));
document.addEventListener('dragleave', e => { if (!e.relatedTarget) screen.classList.remove('drag'); });
document.addEventListener('drop', handleDrop);

flipBtn.addEventListener('click', () => { ensureCtx(); if (ctx.state === 'suspended') ctx.resume(); if (!playing) restartOnNextLoop = true; flip(); });
playBtn.addEventListener('click', () => {
  ensureCtx();
  // armed but held by the browser: this click lets it play (its press may already have resumed it)
  const wasHeld = heldAtPress || (playing && isHeld()); heldAtPress = false;
  if (wasHeld) { ctx.resume(); return; }
  if (playing) { stopTransport(); updateControls(); draw(); } else startTransport();
});
// Space plays and stops, unless a control that uses Space has the focus
document.addEventListener('keydown', e => {
  if (e.code !== 'Space' || e.repeat) return;
  const t = e.target; if (t && t !== document.body && (t.closest('button, [role="slider"], .screen, input, textarea'))) return;
  e.preventDefault(); if (!playBtn.disabled) playBtn.click();
});
$('undo').addEventListener('click', () => { cancelAnim(); undo(); }); $('redo').addEventListener('click', () => { cancelAnim(); redoAction(); });
$('bars4').addEventListener('click', () => { cancelAnim(); state.bars = 4; rerender(true); });
$('bars8').addEventListener('click', () => { cancelAnim(); state.bars = 8; rerender(true); });

// knobs: the photographed cap keeps its lighting; the arc and pointer are live. Vertical drag, wheel, arrow keys.
function knob(id, valueId, get, set, text, positions) {
  const el = $(id), svg = el.querySelector('svg'), value = $(valueId);
  const a0 = Math.PI * 1.25, a1 = Math.PI * 2.75, cx = 56, cy = 56, r = 42;
  const pt = (ang, rad) => [cx + Math.sin(ang) * rad, cy - Math.cos(ang) * rad];
  const arc = (from, to) => { const [x0, y0] = pt(from, r), [x1, y1] = pt(to, r); return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 ${to - from > Math.PI ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`; };
  const paint = () => {
    const v = get(), ang = a0 + v * (a1 - a0), [px, py] = pt(ang, 12), [qx, qy] = pt(ang, 30.5);
    svg.innerHTML = `<path d="${arc(a0, a1)}" fill="none" stroke="#383e41" stroke-width="2.2"/>
      ${v > 0.0001 ? `<path d="${arc(a0, ang)}" fill="none" stroke="#e4e7e9" stroke-width="2.2"/>` : ''}
      <line x1="${px}" y1="${py}" x2="${qx}" y2="${qy}" stroke="rgba(0,0,0,.6)" stroke-width="3.2" stroke-linecap="round"/>
      <line x1="${px}" y1="${py}" x2="${qx}" y2="${qy}" stroke="#e4e7e9" stroke-width="2.2" stroke-linecap="round"/>`;
    value.textContent = text(); el.setAttribute('aria-valuenow', positions ? Math.round(v * (positions - 1)) : Math.round(v * 100));
  };
  let dragging = false, startY = 0, startV = 0;
  el.addEventListener('pointerdown', e => { dragging = true; startY = e.clientY; startV = get(); el.setPointerCapture(e.pointerId); e.preventDefault(); });
  el.addEventListener('pointermove', e => { if (!dragging) return; set(clamp(startV + (startY - e.clientY) / 150, 0, 1)); paint(); });
  el.addEventListener('pointerup', () => { dragging = false; }); el.addEventListener('pointercancel', () => { dragging = false; });
  el.addEventListener('wheel', e => { e.preventDefault(); set(clamp(get() - Math.sign(e.deltaY) * (positions ? 1 / (positions - 1) : 0.02), 0, 1)); paint(); }, { passive: false });
  el.addEventListener('keydown', e => { const step = positions ? 1 / (positions - 1) : 0.02; if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { set(clamp(get() + step, 0, 1)); paint(); e.preventDefault(); } if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { set(clamp(get() - step, 0, 1)); paint(); e.preventDefault(); } });
  paint(); return { paint };
}
const knobs = {
  intensity: knob('k-intensity', 'v-intensity', () => state.intensity, v => { state.intensity = v; if (!state.straight) { cancelAnim(); rerender(false); } }, () => `${Math.round(state.intensity * 100)}%`),
  size: knob('k-size', 'v-size', () => state.sizeChoice / 4, v => { const c = Math.round(v * 4); if (c !== state.sizeChoice) { cancelAnim(); if (state.edits.length) pushHistory(); state.sizeChoice = c; regrid(true); rerender(true); } }, () => SIZE_NAMES[state.sizeChoice], 5),
  age: knob('k-age', 'v-age', () => state.age, v => { state.age = v; rerender(false); }, () => `${Math.round(state.age * 100)}%`),
  movement: knob('k-movement', 'v-movement', () => state.movement, v => { state.movement = v; rerender(false); }, () => `${Math.round(state.movement * 100)}%`)
};

// ============================================================ loading samples
// Decoding runs on an offline context, so a loop can load before anyone clicks; the playback
// context is only created by a click (browsers keep audio silent until then).
let decoder = null;
async function decode(arrayBuffer, name) {
  if (!decoder) { const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext; decoder = new Offline(2, 1, 44100); }
  const buf = await decoder.decodeAudioData(arrayBuffer.slice(0));
  const sr = buf.sampleRate, frames = Math.min(buf.length, Math.floor(120 * sr));
  const L = new Float32Array(frames), R = new Float32Array(frames), mono = new Float32Array(frames);
  buf.copyFromChannel(L, 0, 0); if (buf.numberOfChannels > 1) buf.copyFromChannel(R, 1, 0); else R.set(L);
  for (let i = 0; i < frames; i++) mono[i] = 0.5 * (L[i] + R[i]);
  return { L, R, mono, sr, name };
}
function install(sample, opts = {}) {
  cancelAnim(); beforeLoop = null; queued = null;
  state.sample = sample; state.edits = []; state.history = []; state.redo = []; state.straight = true; state.hovered = -1;
  const ta = performance.now(); regrid(true); perf.analyseMs = Math.round(performance.now() - ta);
  // the worker owns the audio from here; the page keeps the mono mix for analysis
  worker.postMessage({ type: 'sample', L: sample.L, R: sample.R, sr: sample.sr }, [sample.L.buffer, sample.R.buffer]);
  restartOnNextLoop = opts.autoplay !== false;   // a picked sample plays from the start of the sequence
  rerender(true); updateControls(); draw();
}
const nextLoop = () => new Promise(res => { loopWaiters.push(res); setTimeout(res, 4000); });

// ---- the loop tray
const tray = { entries: [], slots: new Map() };
const audioCache = new Map();   // file -> Promise<ArrayBuffer>; decode() copies, so a cached buffer can be reused
let activeFile = null, pendingFile = null, pickToken = 0;
const fetchAudio = file => {
  if (!audioCache.has(file)) audioCache.set(file, fetch(file).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); }).catch(err => { audioCache.delete(file); throw err; }));
  return audioCache.get(file);
};
function syncTiles() {
  for (const [file, slot] of tray.slots) {
    const loaded = file === activeFile, loading = file === pendingFile;
    slot.el.classList.toggle('loaded', loaded); slot.el.classList.toggle('loading', loading);
    slot.state.textContent = loading ? 'LOADING' : loaded ? 'LOADED' : slot.el.classList.contains('error') ? 'TRY AGAIN' : '';
    slot.tile.setAttribute('aria-pressed', loaded);
  }
  $('tray').classList.toggle('has-loop', !!state.sample);
}
// the thumbnail: the loop's real outline, mirrored about the centre like the screen's waveform
function drawThumb(canvas, peaks, cssW, cssH) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.max(1, Math.round(cssW * dpr)); canvas.height = Math.max(1, Math.round(cssH * dpr));
  const g = canvas.getContext('2d'); g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, cssW, cssH);
  const mid = cssH / 2, n = peaks.length, step = cssW / n, bar = Math.max(1, step * 0.62);
  const grad = g.createLinearGradient(0, mid - cssH * 0.42, 0, mid + cssH * 0.42); grad.addColorStop(0, '#edf3f7'); grad.addColorStop(1, '#a9bac9');
  g.fillStyle = grad;
  for (let i = 0; i < n; i++) { const h = Math.max(0.6, peaks[i] / 100 * cssH * 0.42); g.fillRect(i * step + (step - bar) / 2, mid - h, bar, h * 2); }
}
const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// The card leaves the tile, winds up, arcs over the screen and drops into it. It is built at the
// screen's size and scaled down to the tile, so it lands crisp. Resolves with the card at rest.
function throwCard(slot, entry) {
  let target = screen.getBoundingClientRect();
  if (target.top < 0) { frame.scrollIntoView({ block: 'start' }); target = screen.getBoundingClientRect(); }
  const src = slot.screenEl.getBoundingClientRect(), dst = { left: target.left + 3, top: target.top + 3, width: target.width - 6, height: target.height - 6 };
  const card = document.createElement('div'); card.className = 'ghost';
  Object.assign(card.style, { left: `${dst.left}px`, top: `${dst.top}px`, width: `${dst.width}px`, height: `${dst.height}px` });
  const c = document.createElement('canvas'); card.appendChild(c); document.body.appendChild(card);
  drawThumb(c, entry.peaks || [], dst.width, dst.height);
  const sx0 = src.width / dst.width, sy0 = src.height / dst.height, tx0 = src.left - dst.left, ty0 = src.top - dst.top;
  const lerp = (a, b, t) => a + (b - a) * t;
  const sxA = lerp(sx0, 1, 0.72), syA = lerp(sy0, 1, 0.72);
  const cxA = lerp(src.left + src.width / 2, dst.left + dst.width / 2, 0.7), cyA = dst.top + dst.height / 2 - 34;
  const txA = cxA - dst.width * sxA / 2 - dst.left, tyA = cyA - dst.height * syA / 2 - dst.top;
  const t = (x, y, a, sx, sy) => `translate(${x}px, ${y}px) rotate(${a}deg) scale(${sx}, ${sy})`;
  const flight = card.animate([
    { offset: 0,    transform: t(tx0, ty0, 0, sx0, sy0), easing: 'ease-in-out' },
    { offset: 0.14, transform: t(tx0, ty0 + 7, 0, sx0 * 0.97, sy0 * 0.9), easing: 'cubic-bezier(.15,.75,.35,1)' },   // wind-up
    { offset: 0.56, transform: t(txA, tyA, -3.5, sxA, syA), easing: 'cubic-bezier(.55,0,.85,.45)' },                  // apex over the screen
    { offset: 0.86, transform: t(0, 5, 0, 1.012, 0.965), easing: 'ease-out' },                                         // lands, squashes
    { offset: 1,    transform: t(0, 0, 0, 1, 1) }
  ], { duration: 820, fill: 'forwards' });
  return flight.finished.then(() => card);
}
function landFx() {
  screen.classList.remove('landed'); void screen.offsetWidth; screen.classList.add('landed');
  if (!reducedMotion()) frame.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(2px)' }, { transform: 'translateY(0)' }], { duration: 200, easing: 'ease-out' });
}
async function pickEntry(entry, opts = {}) {
  const slot = tray.slots.get(entry.file);
  if (pendingFile === entry.file) return;   // already on its way
  const token = ++pickToken, autoplay = opts.autoplay !== false;
  if (autoplay) { ensureCtx(); if (ctx.state === 'suspended') ctx.resume(); }   // a click: sound is allowed
  if (slot) slot.el.classList.remove('error');
  pendingFile = entry.file; syncTiles();
  const fly = false;   // theme build (2026-10-27): no fly-in card, the picked loop swaps in instantly (user request)
  const flight = fly ? throwCard(slot, entry) : Promise.resolve(null);
  let card = null;
  try {
    const decoded = fetchAudio(entry.file).then(ab => decode(ab, entry.name));
    card = await flight;
    if (card) card.classList.add('waiting');   // still decoding: the card waits in the screen
    const sample = await decoded;
    if (token !== pickToken) { if (card) card.remove(); return; }   // another loop was picked meanwhile
    install(sample, { autoplay });
    await nextLoop();
    activeFile = entry.file; pendingFile = null; syncTiles();
    landFx();
    if (card) { card.classList.remove('waiting'); card.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: 'ease-out', fill: 'forwards' }).finished.then(() => card.remove()); }
  } catch (err) {
    if (card) card.remove();
    if (token !== pickToken) return;
    pendingFile = null; if (slot) slot.el.classList.add('error'); syncTiles();
    toast(`Could not load ${entry.title || entry.name}: ${err.message || err}`);
  }
}
async function loadSampleEntry(entry) { return pickEntry(entry, { fly: false }); }

async function loadTray() {
  try {
    const data = window.SHFDEMO || {}, tiles = $('tiles');   // theme build: the manifest is injected by the page template (asset_url files)
    tray.entries = data.samples || [];
    tray.entries.forEach((entry, i) => {
      const el = document.createElement('div'); el.className = 'slot'; el.style.setProperty('--i', i);
      el.innerHTML = `<div class="lift" aria-hidden="true"><svg viewBox="0 0 22 30"><path d="M4 9 L11 3 L18 9"/><path d="M4 17 L11 11 L18 17"/><path d="M4 25 L11 19 L18 25"/></svg></div>
        <button class="tile" type="button" draggable="true" aria-pressed="false">
          <span class="tile-screen"><canvas></canvas><span class="tile-state"></span></span>
          <span class="tile-name"></span>
          <span class="tile-meta"><span><b>${entry.bpm || ''}</b> BPM</span><span>${entry.key || ''}</span></span>
        </button>`;
      const tile = el.querySelector('.tile'), screenEl = el.querySelector('.tile-screen'), canvas = el.querySelector('canvas');
      el.querySelector('.tile-name').textContent = entry.title || entry.name;
      tile.setAttribute('aria-label', `Load ${entry.title || entry.name}, ${entry.bpm} BPM, ${entry.key || ''}`);
      tile.addEventListener('click', () => pickEntry(entry));
      tile.addEventListener('dragstart', e => { const payload = JSON.stringify(entry); e.dataTransfer.setData('text/x-sample', payload); e.dataTransfer.setData('text/plain', 'sample:' + payload); e.dataTransfer.effectAllowed = 'copy'; });
      tiles.appendChild(el);
      tray.slots.set(entry.file, { el, tile, screenEl, canvas, state: el.querySelector('.tile-state'), entry });
    });
    const paintThumbs = () => { for (const slot of tray.slots.values()) { const r = slot.screenEl.getBoundingClientRect(); if (r.width) drawThumb(slot.canvas, slot.entry.peaks || [], r.width, r.height); } };
    paintThumbs(); window.addEventListener('resize', paintThumbs);
    syncTiles();
    // Astral loads and starts when the page opens. Where the browser holds audio back, it is armed
    // at bar 1 and the first click, tap or key press anywhere lets it play.
    const opening = tray.entries.find(e => e.file === data.autoload) || tray.entries[0];
    if (opening) setTimeout(() => { if (!state.sample && !pendingFile) pickEntry(opening); }, 450);
    // fetch the audio in the background so a pick is near-instant; each loop is about a megabyte
    const idle = window.requestIdleCallback || (f => setTimeout(f, 1200));
    idle(() => tray.entries.reduce((p, e) => p.then(() => fetchAudio(e.file).catch(() => {})), Promise.resolve()));
  } catch (e) { /* no tray */ }
}

(function tick() { if (playing || anim.active) draw(); requestAnimationFrame(tick); })();
loadTray(); updateControls(); draw();
// a small hook for headless checks of the engine (no UI depends on it)
window.__shuffle = { state, loadBuffer: (ab, name) => decode(ab, name).then(install), flip, setLocked, isLocked, undo, redo: redoAction, analyse, render, anim,
  audio: () => ({ ctx, transport, current, playing }) };
})();
